import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { IntegrationDTO, IntegrationFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, DatePickers, IChipsSettingProps } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import moment from 'moment';
import { IntegrationMaintenance } from './IntegrationMaintenance';
import { Message } from '../message/message';

export const Integration: React.FC = () => {

    const [integrationSettingList, setIntegrationSettingList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [integrationList, setIntegrationList] = useState<IntegrationDTO[]>([]);
    const [integrationFilterDTO, setIntegrationFilterDTO] = useState<IntegrationFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setIntegrationFilterDTO({
            ...integrationFilterDTO,
            integrationSettingId: '',
            sourceId: '',
            statusId: '',
            startDateTime: undefined,
            endDateTime: undefined
        } as IntegrationFilterDTO);
    }, []);

    useEffect(() => {
        handleIntegrationGetAllViewAsync();
        handleListGetAll(ListId.linksphere.integration.statusId, setStatusList);
    }, [integrationFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleIntegrationGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        const interval = setInterval(() => {
            handleIntegrationGetAllViewAsync();
        }, 15000);
        return () => clearInterval(interval);
    }, [integrationFilterDTO]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleIntegrationGetAllViewAsync = useCallback(async () => {
        if (integrationFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('linksphere.integration', integrationFilterDTO, setActionExecuted, setLoading, setIntegrationList, showMessage, true);
    }, [integrationFilterDTO]);

    const handleClearFilterClick = () => {
        setIntegrationFilterDTO({
            ...integrationFilterDTO,
            integrationSettingId: '',
            sourceId: '',
            statusId: '',
            startDateTime: undefined,
            endDateTime: undefined
        } as IntegrationFilterDTO);
    };

    function handleSearchClick() {
        handleIntegrationGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Integration', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: integrationList.length },
        { label: 'Start Execution', value: integrationFilterDTO?.startDateTime ? moment(integrationFilterDTO.startDateTime).format('DD/MM/YYYY') : '' },
        { label: 'End Execution', value: integrationFilterDTO?.endDateTime ? moment(integrationFilterDTO.endDateTime).format('DD/MM/YYYY') : '' },
        { label: 'Status', value: statusList.find(x => x.id === integrationFilterDTO?.statusId)?.value },

    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: 'Waiting for processing', iconName: 'schedule_outlined', iconColor: 'primary' },
        { value: 'Processing', iconName: 'update_outlined', iconColor: 'info' },
        { value: 'Successfully integrated', iconName: 'check_circle_outlined', iconColor: 'success' },
        { value: 'Integration failed', iconName: 'running_with_errors_outlined', iconColor: 'error' },
        { value: 'Reprocessing Integration', iconName: 'manage_history_outlined', iconColor: 'warning' }
    ];

    const columns: ICardColumns[] = [
        { field: 'integrationSetting', label: 'Integration Setting', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'integrationSettingId', label: 'Integration Setting Id', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceId', label: 'Source Id', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: 'Status', size: 2, type: 'text', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'startDateTime', label: 'Start Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'endDateTime', label: 'End Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'elapseTime', label: 'ElapseTime', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='INTEGRATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='integrationSettingId'
                                label='Integration Setting'
                                setValue={setIntegrationFilterDTO}
                                value={integrationFilterDTO?.integrationSettingId}
                                obj={integrationFilterDTO}
                                valueList={integrationSettingList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='integrationSettingId'
                                label='Integration Setting Id'
                                setValue={setIntegrationFilterDTO}
                                value={integrationFilterDTO?.integrationSettingId}
                                obj={integrationFilterDTO}
                                placeholder={'IntegrationSetting Id'}
                                maxLength={200}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='statusId'
                                label='Status'
                                setValue={setIntegrationFilterDTO}
                                value={integrationFilterDTO?.statusId}
                                obj={integrationFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='sourceId'
                                label='Source Id'
                                setValue={setIntegrationFilterDTO}
                                value={integrationFilterDTO?.sourceId}
                                obj={integrationFilterDTO}
                                placeholder={'Source Id'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='startDateTime'
                                label='Start Date Time'
                                value={integrationFilterDTO?.startDateTime ?? null}
                                setValue={setIntegrationFilterDTO}
                                obj={integrationFilterDTO}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='endDateTime'
                                label='End Date Time'
                                value={integrationFilterDTO?.endDateTime ?? null}
                                setValue={setIntegrationFilterDTO}
                                obj={integrationFilterDTO}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={integrationList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'Associate Messages'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <Message data={expadedData} />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <IntegrationMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <IntegrationMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'linksphere.integration'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};